import { createContext, useState, useCallback, useMemo, useEffect, use } from 'react';
import { useOrderSources } from '@hooks/orderSources';
import type { ApiContact, ContactBillingAddress } from '@api/contacts';
import type { OrderSource } from '@api/orderSources';
import { defaultBillingAddress, withLegacyBillingFallback } from './billingAddressFallback';
import { useContactBillingDetail } from './useContactBillingDetail';
import { createLogger } from '@/utils/logger';

const log = createLogger('CreateOrder');

export type ModalView = 'create-order' | 'create-customer' | 'create-source' | 'create-billing-address';

export interface CreateOrderDraft {
  customerName: string;
  phoneNumber: string;
  // GRAIN-2485: the resolved org attached to the ORDER. `organisation` is the
  // display name; `organisationId` is what gets sent as `organisation_id`.
  // Both prefill from the contact's primary org and clear together (individual
  // order). `organisationId === null` => no org attached.
  organisation: string;
  organisationId: number | null;
  customerNote: string;
  billingAddress: string;
  postalCode: string;
  billingAddresses: ContactBillingAddress[];
  selectedBillingAddressId: number | null;
  orderType: string;
  salespersonId?: number;
}

interface CreateOrderState {
  view: ModalView;
  selectedContact: ApiContact | null;
  selectedSourceId: string;
  orderSources: OrderSource[];
  orderDraft: CreateOrderDraft;
  isBillingAddressLoading: boolean;
  selectedBillingAddress: ContactBillingAddress | undefined;
}

interface CreateOrderActions {
  setView: (view: ModalView) => void;
  setSelectedContact: (contact: ApiContact | null) => void;
  setSelectedSourceId: (id: string) => void;
  updateOrderDraft: (draft: Partial<CreateOrderDraft>) => void;
  applyCreatedBillingAddress: (contact: ApiContact, billingAddress: ContactBillingAddress) => void;
  reset: () => void;
}

interface CreateOrderContextValue {
  state: CreateOrderState;
  actions: CreateOrderActions;
}

const CreateOrderContext = createContext<CreateOrderContextValue | null>(null);

export function useCreateOrderContext() {
  const ctx = use(CreateOrderContext);
  if (!ctx) throw new Error('useCreateOrderContext must be used within CreateOrderProvider');
  return ctx;
}

interface CreateOrderProviderProps {
  children: React.ReactNode;
  initialContact?: ApiContact | null;
}

const PHONE_PREFIX_RE = /^\+65\s*/;

const DEFAULT_ORDER_DRAFT: CreateOrderDraft = {
  customerName: '',
  phoneNumber: '',
  organisation: '',
  organisationId: null,
  customerNote: '',
  billingAddress: '',
  postalCode: '',
  billingAddresses: [],
  selectedBillingAddressId: null,
  orderType: 'inbound',
  salespersonId: undefined,
};

function stripPhonePrefix(value: string | null): string {
  return (value ?? '').replace(PHONE_PREFIX_RE, '');
}

function billingAddressDraft(addresses: ContactBillingAddress[]): Pick<
  CreateOrderDraft,
  'billingAddresses' | 'selectedBillingAddressId' | 'billingAddress' | 'postalCode'
> {
  const selectedAddress = defaultBillingAddress(addresses);

  return {
    billingAddresses: addresses,
    selectedBillingAddressId: selectedAddress?.id ?? null,
    billingAddress: selectedAddress?.address ?? '',
    postalCode: selectedAddress?.postal_code ?? '',
  };
}

function contactDraft(
  contact: ApiContact | null,
  billingAddresses: ContactBillingAddress[] = contact?.billing_addresses ?? [],
): Pick<
  CreateOrderDraft,
  | 'customerName'
  | 'phoneNumber'
  | 'organisation'
  | 'organisationId'
  | 'customerNote'
  | 'billingAddresses'
  | 'selectedBillingAddressId'
  | 'billingAddress'
  | 'postalCode'
> {
  if (!contact) {
    return {
      customerName: '',
      phoneNumber: '',
      organisation: '',
      organisationId: null,
      customerNote: '',
      ...billingAddressDraft([]),
    };
  }

  const effectiveAddresses = withLegacyBillingFallback(billingAddresses, {
    address: contact.billing_address,
    postalCode: contact.billing_postal_code,
  });

  return {
    customerName: contact.full_name ?? '',
    phoneNumber: stripPhonePrefix(contact.mobile_number),
    organisation: contact.organisation?.name ?? '',
    organisationId: contact.organisation?.id ?? null,
    customerNote: contact.notes ?? '',
    ...billingAddressDraft(effectiveAddresses),
  };
}

function initialOrderDraft(contact: ApiContact | null): CreateOrderDraft {
  return {
    ...DEFAULT_ORDER_DRAFT,
    ...contactDraft(contact),
  };
}

export function CreateOrderProvider({ children, initialContact = null }: CreateOrderProviderProps) {
  const [view, setView] = useState<ModalView>('create-order');
  const [selectedContact, setSelectedContact] = useState<ApiContact | null>(initialContact);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [orderDraft, setOrderDraft] = useState<CreateOrderDraft>(() => initialOrderDraft(initialContact));

  // Dropdown wants every active source — request a single page large enough
  // to cover the catalog (no UI pagination for the picker).
  const { data: orderSourcesData } = useOrderSources({
    params: { status: 'active', perPage: 100 },
  });
  const orderSources = orderSourcesData?.order_sources ?? [];

  // The picker has no pagination, so if active sources ever exceed the page
  // size the dropdown silently drops options. Surface that so we can bump
  // perPage (Listable caps it at 200) or add pagination before users notice.
  const totalActive = orderSourcesData?.pagination?.total ?? orderSources.length;
  useEffect(() => {
    if (totalActive > orderSources.length) {
      log.warn(
        `order-source picker truncated: ${totalActive} active sources but only ${orderSources.length} loaded — bump perPage or paginate.`,
      );
    }
  }, [totalActive, orderSources.length]);

  const { isLoading: isBillingAddressLoading, fetchDetail, cancel } = useContactBillingDetail<void>({
    onResolved: (detailContact) => {
      setSelectedContact(detailContact);
      setOrderDraft((draft) => ({
        ...draft,
        ...contactDraft(detailContact, detailContact.billing_addresses ?? []),
      }));
    },
    // No onError: selectContact already applied the legacy-fallback draft
    // synchronously before this fetch started, so a failure just leaves that
    // draft in place — submit stays gated on isBillingAddressLoading either way.
  });

  const selectContact = useCallback((contact: ApiContact | null) => {
    setSelectedContact(contact);
    setOrderDraft((draft) => ({
      ...draft,
      ...contactDraft(contact),
    }));

    if (!contact) {
      cancel();
      return;
    }

    fetchDetail(contact.id, undefined);
  }, [cancel, fetchDetail]);

  const updateOrderDraft = useCallback((draft: Partial<CreateOrderDraft>) => {
    setOrderDraft((current) => ({ ...current, ...draft }));
  }, []);

  const applyCreatedBillingAddress = useCallback((
    contact: ApiContact,
    billingAddress: ContactBillingAddress,
  ) => {
    // Abort any in-flight detail fetch (and clear its loading flag) so a
    // late-resolving fetch can't clobber the address we just created.
    cancel();
    setSelectedContact(contact);
    setOrderDraft((draft) => ({
      ...draft,
      billingAddresses: contact.billing_addresses ?? [],
      selectedBillingAddressId: billingAddress.id,
      billingAddress: billingAddress.address,
      postalCode: billingAddress.postal_code,
    }));
  }, [cancel]);

  const reset = useCallback(() => {
    cancel();
    setView('create-order');
    setSelectedContact(null);
    setSelectedSourceId('');
    setOrderDraft(DEFAULT_ORDER_DRAFT);
  }, [cancel]);

  const state = useMemo<CreateOrderState>(
    () => ({
      view,
      selectedContact,
      selectedSourceId,
      orderSources: orderSources ?? [],
      orderDraft,
      isBillingAddressLoading,
      selectedBillingAddress: orderDraft.billingAddresses.find(
        (billingAddress) => String(billingAddress.id) === String(orderDraft.selectedBillingAddressId ?? ''),
      ),
    }),
    [view, selectedContact, selectedSourceId, orderSources, orderDraft, isBillingAddressLoading],
  );

  const actions = useMemo<CreateOrderActions>(
    () => ({
      setView,
      setSelectedContact: selectContact,
      setSelectedSourceId,
      updateOrderDraft,
      applyCreatedBillingAddress,
      reset,
    }),
    [selectContact, updateOrderDraft, applyCreatedBillingAddress, reset],
  );

  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return (
    <CreateOrderContext value={value}>
      {children}
    </CreateOrderContext>
  );
}
